import L from 'leaflet';

/**
 * @param {unknown} type
 * @returns {boolean}
 */
export function isAtmScreenType(type) {
    return String(type ?? '').trim().toUpperCase() === 'YAXITV';
}

/**
 * Leaflet map marker: TV screen by default, ATM when Type is YAXITV.
 * @param {{ isActive?: boolean, type?: unknown }} options
 * @returns {import('leaflet').Icon}
 */
export function buildScreenMapMarkerIcon({ isActive = false, type } = {}) {
    const markerColor = isActive ? '#28a745' : '#6c757d';
    const bgColor = isActive ? '#d4edda' : '#e9ecef';
    const indicatorColor = isActive ? '#28a745' : '#dc3545';
    const useAtm = isAtmScreenType(type);

    const svg = useAtm
        ? `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 48" width="40" height="48">
                <!-- ATM body -->
                <rect x="4" y="2" width="32" height="44" rx="3" fill="${bgColor}" stroke="${markerColor}" stroke-width="2.5"/>
                <!-- Display -->
                <rect x="8" y="6" width="24" height="14" rx="1.5" fill="${markerColor}" opacity="0.65"/>
                <!-- Card slot -->
                <rect x="10" y="24" width="20" height="3" rx="1" fill="${markerColor}"/>
                <!-- Keypad -->
                <rect x="10" y="30" width="5" height="4" rx="0.8" fill="${markerColor}" opacity="0.85"/>
                <rect x="17.5" y="30" width="5" height="4" rx="0.8" fill="${markerColor}" opacity="0.85"/>
                <rect x="25" y="30" width="5" height="4" rx="0.8" fill="${markerColor}" opacity="0.85"/>
                <rect x="10" y="36" width="5" height="4" rx="0.8" fill="${markerColor}" opacity="0.85"/>
                <rect x="17.5" y="36" width="5" height="4" rx="0.8" fill="${markerColor}" opacity="0.85"/>
                <rect x="25" y="36" width="5" height="4" rx="0.8" fill="${markerColor}" opacity="0.85"/>
                <!-- Activity indicator -->
                <circle cx="34" cy="6" r="3" fill="${indicatorColor}" stroke="white" stroke-width="1"/>
            </svg>
        `
        : `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 36" width="48" height="36">
                <rect x="2" y="2" width="44" height="28" rx="2" fill="${bgColor}" stroke="${markerColor}" stroke-width="2.5"/>
                <rect x="5" y="5" width="38" height="22" rx="1" fill="${markerColor}" opacity="0.6"/>
                <rect x="19" y="30" width="10" height="2" rx="1" fill="${markerColor}"/>
                <rect x="22" y="28" width="4" height="4" fill="${markerColor}"/>
                <circle cx="42" cy="6" r="3" fill="${indicatorColor}" stroke="white" stroke-width="1"/>
            </svg>
        `;

    return L.icon({
        iconUrl: `data:image/svg+xml;base64,${btoa(svg)}`,
        iconSize: useAtm ? [40, 48] : [48, 36],
        iconAnchor: useAtm ? [20, 48] : [24, 36],
        popupAnchor: useAtm ? [0, -48] : [0, -36],
        className: useAtm ? 'screen-marker atm-marker' : 'screen-marker',
    });
}

/**
 * Data-URI SVG for non-Leaflet UI (e.g. admin screen details status icon).
 * @param {{ isActive?: boolean, type?: unknown, width?: number, height?: number }} options
 * @returns {string}
 */
export function buildScreenStatusIconSrc({ isActive = false, type, width, height } = {}) {
    const markerColor = isActive ? '#28a745' : '#6c757d';
    const bgColor = isActive ? '#d4edda' : '#e9ecef';
    const indicatorColor = isActive ? '#28a745' : '#dc3545';
    const useAtm = isAtmScreenType(type);
    const w = width ?? (useAtm ? 400 : 480);
    const h = height ?? (useAtm ? 480 : 360);

    const svg = useAtm
        ? `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 48" width="${w}" height="${h}">
                <rect x="4" y="2" width="32" height="44" rx="3" fill="${bgColor}" stroke="${markerColor}" stroke-width="2.5"/>
                <rect x="8" y="6" width="24" height="14" rx="1.5" fill="${markerColor}" opacity="0.65"/>
                <rect x="10" y="24" width="20" height="3" rx="1" fill="${markerColor}"/>
                <rect x="10" y="30" width="5" height="4" rx="0.8" fill="${markerColor}" opacity="0.85"/>
                <rect x="17.5" y="30" width="5" height="4" rx="0.8" fill="${markerColor}" opacity="0.85"/>
                <rect x="25" y="30" width="5" height="4" rx="0.8" fill="${markerColor}" opacity="0.85"/>
                <rect x="10" y="36" width="5" height="4" rx="0.8" fill="${markerColor}" opacity="0.85"/>
                <rect x="17.5" y="36" width="5" height="4" rx="0.8" fill="${markerColor}" opacity="0.85"/>
                <rect x="25" y="36" width="5" height="4" rx="0.8" fill="${markerColor}" opacity="0.85"/>
                <circle cx="34" cy="6" r="3" fill="${indicatorColor}" stroke="white" stroke-width="1"/>
            </svg>
        `
        : `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 36" width="${w}" height="${h}">
                <rect x="2" y="2" width="44" height="28" rx="2" fill="${bgColor}" stroke="${markerColor}" stroke-width="2.5"/>
                <rect x="5" y="5" width="38" height="22" rx="1" fill="${markerColor}" opacity="0.6"/>
                <rect x="19" y="30" width="10" height="2" rx="1" fill="${markerColor}"/>
                <rect x="22" y="28" width="4" height="4" fill="${markerColor}"/>
                <circle cx="42" cy="6" r="3" fill="${indicatorColor}" stroke="white" stroke-width="1"/>
            </svg>
        `;

    return `data:image/svg+xml;base64,${btoa(svg)}`;
}
